interface CacheEntry {
  data: unknown;
  fetchedAt: number;
}

const STORAGE_PREFIX = 'zt-admin-cache:';

class DataCache {
  private memory = new Map<string, CacheEntry>();

  get<T>(key: string, ttlMs: number): T | null {
    const memoryEntry = this.memory.get(key);
    if (memoryEntry) {
      const value = this.resolveEntry<T>(key, ttlMs, memoryEntry);
      if (value !== null) return value;
    }

    try {
      const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
      if (!raw) return null;
      const entry = JSON.parse(raw) as CacheEntry;
      const value = this.resolveEntry<T>(key, ttlMs, entry);
      if (value !== null) {
        this.memory.set(key, entry);
        return value;
      }
      sessionStorage.removeItem(STORAGE_PREFIX + key);
    } catch {
      sessionStorage.removeItem(STORAGE_PREFIX + key);
    }
    return null;
  }

  set<T>(key: string, data: T): void {
    const entry: CacheEntry = { data, fetchedAt: Date.now() };
    this.memory.set(key, entry);
    try {
      sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch {
      // sessionStorage doluysa sadece memory cache kullan
    }
  }

  invalidate(key: string): void {
    this.memory.delete(key);
    sessionStorage.removeItem(STORAGE_PREFIX + key);
  }

  invalidatePattern(pattern: RegExp): void {
    for (const key of Array.from(this.memory.keys())) {
      if (pattern.test(key)) this.invalidate(key);
    }
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const storageKey = sessionStorage.key(i);
      if (storageKey?.startsWith(STORAGE_PREFIX)) {
        const key = storageKey.slice(STORAGE_PREFIX.length);
        if (pattern.test(key)) this.invalidate(key);
      }
    }
  }

  private resolveEntry<T>(key: string, ttlMs: number, entry: CacheEntry): T | null {
    if (Date.now() - entry.fetchedAt > ttlMs) {
      this.invalidate(key);
      return null;
    }
    return entry.data as T;
  }
}

export const dataCache = new DataCache();

export const CACHE_TTL = {
  dashboard: 5 * 60 * 1000,
  pendingProducts: 3 * 60 * 1000,
  users: 3 * 60 * 1000,
  searchResults: 60 * 1000,
  lastProductId: 2 * 60 * 1000,
  barcodePositive: 24 * 60 * 60 * 1000,
} as const;
